'use client';

import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Eye, Edit, CalendarPlus, Linkedin, MoreHorizontal, MessageSquare, Sparkles } from 'lucide-react';
import { Contact, OutreachStage, LinkedInStatus, MeetingStatus } from './mockContacts';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface Props {
  contacts: Contact[];
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  sortKey: keyof Contact;
  sortDir: 'asc' | 'desc';
  onSort: (key: keyof Contact) => void;
  onOpenAI: (contact: Contact) => void;
}

// Stage → badge class mapping
const stageClass: Record<OutreachStage, string> = {
  'New Lead': 'stage-new',
  'Identified': 'stage-identified',
  'Researching': 'stage-researching',
  'LinkedIn Requested': 'stage-linkedin-requested',
  'LinkedIn Connected': 'stage-linkedin-connected',
  'Intro Sent': 'stage-intro-sent',
  'TL Shared': 'stage-tl-sent',
  'Meeting Requested': 'stage-meeting-requested',
  'Meeting Confirmed': 'stage-meeting-confirmed',
  'Met at Event': 'stage-met-at-event',
  'Follow-up Sent': 'stage-followup-sent',
  'Opportunity Created': 'stage-opportunity',
  'Closed / No Response': 'stage-closed',
  'Not Relevant': 'stage-not-relevant',
};

const linkedinClass: Record<LinkedInStatus, string> = {
  'Not Attempted': 'linkedin-none',
  'Connection Planned': 'linkedin-none',
  'Request Sent': 'linkedin-sent',
  'Connected': 'linkedin-connected',
  'Message Sent': 'linkedin-sent',
  'Replied': 'linkedin-replied',
  'No Response': 'linkedin-none',
  'Follow-up Needed': 'linkedin-sent',
};

const meetingClass: Record<MeetingStatus, string> = {
  'None': 'meeting-none',
  'Requested': 'meeting-requested',
  'Tentative': 'meeting-requested',
  'Confirmed': 'meeting-confirmed',
  'Completed': 'meeting-completed',
  'Cancelled': 'meeting-cancelled',
  'No-show': 'meeting-cancelled',
};

const priorityDot: Record<string, string> = {
  High: 'bg-red-500',
  Medium: 'bg-amber-500',
  Low: 'bg-slate-400',
};

function SortIcon({ col, sortKey, sortDir }: { col: keyof Contact; sortKey: keyof Contact; sortDir: 'asc' | 'desc' }) {
  if (col !== sortKey) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
  return sortDir === 'asc'
    ? <ChevronUp size={12} className="text-primary" />
    : <ChevronDown size={12} className="text-primary" />;
}

function ScorePill({ score }: { score: number }) {
  const color = score >= 80 ? 'bg-emerald-100 text-emerald-800' : score >= 60 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold ${color}`}>
      {score}
    </span>
  );
}

const PAGE_SIZE = 10;

export default function ContactsTable({
  contacts, selectedIds, onToggleSelect, onToggleAll, sortKey, sortDir, onSort, onOpenAI,
}: Props) {
  const [page, setPage] = useState(1);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const totalPages = Math.ceil(contacts.length / PAGE_SIZE);
  const pageContacts = contacts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const allSelected = contacts.length > 0 && selectedIds.length === contacts.length;

  const cols: { key: keyof Contact; label: string; sortable: boolean }[] = [
    { key: 'firstName', label: 'Contact', sortable: true },
    { key: 'company', label: 'Company', sortable: true },
    { key: 'title', label: 'Title', sortable: false },
    { key: 'outreachStage', label: 'Stage', sortable: true },
    { key: 'linkedinStatus', label: 'LinkedIn', sortable: true },
    { key: 'meetingStatus', label: 'Meeting', sortable: true },
    { key: 'priorityScore', label: 'Score', sortable: true },
    { key: 'event', label: 'Event', sortable: true },
    { key: 'lastActivity', label: 'Last Activity', sortable: true },
    { key: 'nextActionDate', label: 'Next Action', sortable: true },
  ];

  return (
    <div className="px-6 pb-6">
      {contacts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4">
            <MessageSquare size={22} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-semibold text-foreground">No contacts match your filters</p>
          <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or clearing filters</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border bg-white shadow-card mt-3">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="table-th w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={onToggleAll}
                      className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
                    />
                  </th>
                  {cols.map((col) => (
                    <th
                      key={`th-${col.key}`}
                      className={`table-th ${col.sortable ? 'cursor-pointer hover:text-foreground select-none' : ''} ${sortKey === col.key ? 'text-primary' : ''}`}
                      onClick={() => col.sortable && onSort(col.key)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {col.sortable && <SortIcon col={col.key} sortKey={sortKey} sortDir={sortDir} />}
                      </div>
                    </th>
                  ))}
                  <th className="table-th w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageContacts.map((contact) => {
                  const isSelected = selectedIds.includes(contact.id);
                  const isHovered = hoveredRow === contact.id;
                  const isOverdue = contact.nextActionDate < '2026-03-23';

                  return (
                    <tr
                      key={contact.id}
                      className={`table-row ${isSelected ? 'bg-primary/5' : ''}`}
                      onMouseEnter={() => setHoveredRow(contact.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="table-td w-10">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleSelect(contact.id)}
                          className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
                        />
                      </td>

                      {/* Contact name */}
                      <td className="table-td">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-[10px] font-semibold text-primary">
                              {contact.firstName[0]}{contact.lastName[0]}
                            </span>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-foreground">
                              {contact.firstName} {contact.lastName}
                            </p>
                            <p className="text-[10px] text-muted-foreground">{contact.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Company */}
                      <td className="table-td">
                        <p className="text-xs font-medium text-foreground">{contact.company}</p>
                        <p className="text-[10px] text-muted-foreground">{contact.industry}</p>
                      </td>

                      {/* Title */}
                      <td className="table-td">
                        <p className="text-xs text-foreground max-w-[160px] truncate" title={contact.title}>
                          {contact.title}
                        </p>
                      </td>

                      {/* Outreach Stage */}
                      <td className="table-td">
                        <span className={`badge ${stageClass[contact.outreachStage]}`}>
                          {contact.outreachStage}
                        </span>
                      </td>

                      {/* LinkedIn */}
                      <td className="table-td">
                        <span className={`badge ${linkedinClass[contact.linkedinStatus]}`}>
                          {contact.linkedinStatus}
                        </span>
                      </td>

                      {/* Meeting */}
                      <td className="table-td">
                        <span className={`badge ${meetingClass[contact.meetingStatus]}`}>
                          {contact.meetingStatus}
                        </span>
                      </td>

                      {/* Score */}
                      <td className="table-td">
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${priorityDot[contact.priority]}`} />
                          <ScorePill score={contact.priorityScore} />
                        </div>
                      </td>

                      {/* Event */}
                      <td className="table-td">
                        <span className="text-[11px] text-muted-foreground max-w-[120px] truncate block" title={contact.event}>
                          {contact.event}
                        </span>
                      </td>

                      {/* Last Activity */}
                      <td className="table-td">
                        <span className="text-xs font-mono text-muted-foreground">
                          {contact.lastActivity}
                        </span>
                      </td>

                      {/* Next Action */}
                      <td className="table-td">
                        <span className={`text-xs font-mono font-medium ${isOverdue ? 'text-red-600' : 'text-foreground'}`}>
                          {contact.nextActionDate}
                          {isOverdue && <span className="ml-1 text-[9px] text-red-500">OVERDUE</span>}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="table-td text-right">
                        <div className={`flex items-center justify-end gap-0.5 transition-opacity duration-150 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                          <ActionButton
                            icon={Eye}
                            label="View contact"
                            onClick={() => toast.info(`Viewing ${contact.firstName} ${contact.lastName}`)}
                          />
                          <ActionButton
                            icon={Edit}
                            label="Edit contact"
                            onClick={() => toast.info(`Editing ${contact.firstName} ${contact.lastName}`)}
                          />
                          <ActionButton
                            icon={Linkedin}
                            label="LinkedIn action"
                            onClick={() => toast.success(`LinkedIn action logged for ${contact.firstName}`)}
                          />
                          <ActionButton
                            icon={CalendarPlus}
                            label="Request meeting"
                            onClick={() => toast.success(`Meeting request created for ${contact.firstName}`)}
                          />
                          <ActionButton
                            icon={Sparkles}
                            label="AI Assistant"
                            onClick={() => onOpenAI(contact)}
                          />
                          <ActionButton
                            icon={MoreHorizontal}
                            label="More actions"
                            onClick={() => toast.info('More actions')}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-xs text-muted-foreground">
              Showing <span className="font-mono font-semibold text-foreground">{(page - 1) * PAGE_SIZE + 1}</span>–
              <span className="font-mono font-semibold text-foreground">{Math.min(page * PAGE_SIZE, contacts.length)}</span> of{' '}
              <span className="font-mono font-semibold text-foreground">{contacts.length}</span> contacts
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 text-xs border border-border rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={`page-${p}`}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 text-xs rounded-lg font-medium transition-colors ${
                    p === page
                      ? 'bg-primary text-white' :'border border-border hover:bg-muted text-foreground'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 text-xs border border-border rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ActionButton({
  icon: Icon, label, onClick,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      title={label}
      onClick={onClick}
      className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 active:scale-95"
      aria-label={label}
    >
      <Icon size={13} strokeWidth={1.8} />
    </button>
  );
}